import type { DesignReferenceDto } from '@carbonwebmail/contracts'
import { useRef } from 'react'
import { Alert } from '../../ui/Alert.js'

/**
 * Files handed to the designer to work from.
 *
 * **The point is that a design should not have to be described in words when it
 * already exists in a file.** A brand sheet, last year's newsletter, the logo,
 * a screenshot of the website — the model reads them, and what it understands
 * about the sender is kept, so the next design starts from it.
 *
 * Each attached file can be taken off again before sending, because attaching
 * the wrong screenshot and having no way back would mean starting the
 * conversation over.
 */
export function Attachments({
  references,
  refused,
  busy,
  onAdd,
  onRemove,
}: {
  readonly references: readonly DesignReferenceDto[]
  /** Why a file was not attached. One line each, about the last pick only. */
  readonly refused: readonly string[]
  readonly busy: boolean
  readonly onAdd: (files: readonly File[]) => void
  readonly onRemove: (name: string) => void
}) {
  const picker = useRef<HTMLInputElement>(null)

  return (
    <div className="mt-3">
      <input
        accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/html,text/css,text/markdown,text/csv,application/json"
        className="sr-only"
        id="designer-references"
        multiple
        onChange={(event) => {
          onAdd([...(event.target.files ?? [])])
          /*
           * Cleared so picking the same file twice fires a change.
           * A file input keeps its value, and "attach, remove, attach the same
           * one again" would otherwise do nothing the third time.
           */
          event.target.value = ''
        }}
        ref={picker}
        type="file"
      />

      <div className="flex flex-wrap items-center gap-2">
        <button
          className="rounded-pill border border-line px-3.5 py-1.5 font-semibold text-[13px] text-t2 hover:text-t1 disabled:opacity-60"
          disabled={busy}
          onClick={() => picker.current?.click()}
          type="button"
        >
          Attach files
        </button>
        <span className="text-[12px] text-t3">
          Images, PDFs or text. A brand sheet, a logo, an email you already like.
        </span>
      </div>

      {references.length === 0 ? null : (
        <ul className="mt-2.5 flex flex-wrap gap-2">
          {references.map((reference) => (
            <li
              className="flex items-center gap-2 rounded-pill border border-line bg-surface-2 py-1 pr-1.5 pl-3 text-[12px] text-t2"
              key={reference.name}
            >
              <span className="max-w-56 truncate">{reference.name}</span>
              <button
                aria-label={`Remove ${reference.name}`}
                className="rounded-pill px-1.5 text-t3 hover:text-danger"
                disabled={busy}
                onClick={() => onRemove(reference.name)}
                type="button"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {refused.length === 0 ? null : (
        <div className="mt-2.5">
          <Alert tone="unknown">
            {refused.map((line) => (
              <span className="block" key={line}>
                {line}
              </span>
            ))}
          </Alert>
        </div>
      )}
    </div>
  )
}
